/**
 * O caminho que a pessoa pediu, guardado até depois do login.
 *
 * ## O que estava quebrado
 *
 * Quem clicava no link de um curso sem sessão caía em `/entrar` e, depois de
 * entrar, ia parar no seletor de áreas — `Entrar` fazia `navegar('/')` fixo, sem
 * olhar de onde a pessoa veio. O link que o dono do curso mandou por e-mail
 * cumpria metade do trabalho: identificava a área e perdia o destino.
 *
 * ## Por que o destino viaja na busca, e não em `state`
 *
 * Porque a tela de entrar tem três saídas — entrar, primeiro acesso e esqueci a
 * senha — e as duas últimas são `<Link>` que só carregam `window.location.search`.
 * Guardar o destino no `state` do router o perderia em qualquer uma dessas
 * travessias, que são justamente as de quem nunca entrou: o comprador novo.
 *
 * ## Por que a leitura recusa endereço absoluto
 *
 * `?destino=https://outro-site` faria a nossa tela de login mandar a pessoa
 * autenticada para fora — redirecionamento aberto clássico, e com o agravante de
 * o link parecer nosso. `//host` é a mesma coisa escrita de outro jeito: o
 * navegador o lê como protocolo relativo. Os dois caem na raiz.
 *
 * ## Por que a regra não está mais escrita aqui
 *
 * Porque ela era escrita em três lugares, e a auditoria nº 5 mostrou o que isso
 * custa: as outras duas cópias deixavam passar `/\evil.com`, que o parser de URL
 * resolve para `https://evil.com`. A regra agora é uma só
 * (`@vestigo/core/caminho-interno`), com os testes dela — e esta lista de casos
 * cresce lá, para os três lugares de uma vez.
 */
import { caminhoInternoOuRaiz } from '@vestigo/core/caminho-interno';

/** Monta o endereço da tela de entrar com a área e, quando há, o destino. */
export function enderecoDeEntrar(areaSlug: string | undefined, destino?: string): string {
  const q = new URLSearchParams();
  if (areaSlug) q.set('a', areaSlug);
  /* A raiz é o padrão de quem entra; gravá-la seria ruído na barra de endereço. */
  if (destino && destino !== '/') q.set('destino', destino);
  const s = q.toString();
  return s ? `/entrar?${s}` : '/entrar';
}

/** Para onde ir depois de entrar. Sempre um caminho interno; nunca outro site. */
export function destinoGuardado(busca: string): string {
  return caminhoInternoOuRaiz(new URLSearchParams(busca).get('destino'));
}
